// SID 替换策略表单的草稿与换算（决策 D154，docs/04 第 2 节）。前端只做格式检查，正则能否编译、目标的 GSM 字符与引用是否存在由保存时的快照校验负责（422）。
import type { Errors } from "@/app/useDraft";
import { validMcc, validMnc } from "@/app/form";
import { senderLabel, type EntityStatus, type SenderMapping, type SenderPattern, type SenderPolicy, type SenderPolicyInput } from "@/app/master";

/** 映射与正则的一行；key 只给前端做行身份。 */
export interface MappingRow extends SenderMapping { key: string }
export interface PatternRow extends SenderPattern { key: string }

export interface SenderPolicyDraft {
  name: string; priority: string; account_ids: string[]; channel_ids: string[]; mcc: string; mnc: string;
  mappings: MappingRow[]; patterns: PatternRow[]; default_enabled: boolean; default_target: string; status: EntityStatus; note: string;
}

/** 优先级的上限：表上是 integer。 */
const MAX_PRIORITY = 2_147_483_647;

let seq = 0;
const nextKey = () => `r${++seq}`;

/** 空的一行映射或正则。 */
export const newMapping = (): MappingRow => ({ key: nextKey(), from: "", to: "" });
export const newPattern = (): PatternRow => ({ key: nextKey(), pattern: "", to: "" });

export function senderPolicyDraft(row?: SenderPolicy): SenderPolicyDraft {
  if (!row) return { name: "", priority: "", account_ids: [], channel_ids: [], mcc: "", mnc: "", mappings: [], patterns: [], default_enabled: false, default_target: "", status: "active", note: "" };
  return {
    name: row.name, priority: String(row.priority), account_ids: row.account_ids.map(String), channel_ids: row.channel_ids.map(String), mcc: row.mcc, mnc: row.mnc,
    mappings: row.mappings.map((m) => ({ ...m, key: nextKey() })), patterns: row.patterns.map((p) => ({ ...p, key: nextKey() })),
    default_enabled: row.default_enabled, default_target: row.default_target, status: row.status, note: row.note ?? "",
  };
}

/** 目标 sender 的格式（D154 第 3 条）：可以为空；纯数字不超过 15 位，否则不超过 11 个字符。GSM 字符由服务端校验。带 $ 引用的正则目标运行时展开后再校验。 */
export function senderTargetError(to: string): string | undefined {
  if (!to) return undefined;
  if (/^\d+$/.test(to)) return to.length > 15 ? "纯数字 sender 不超过 15 位。" : undefined;
  return [...to].length > 11 ? "字母数字 sender 不超过 11 个字符。" : undefined;
}

/** 逐行错误：映射与正则各一份，按行下标。 */
export interface RowErrors { mappings: (string | undefined)[]; patterns: (string | undefined)[] }

export function senderPolicyRowErrors(d: SenderPolicyDraft): RowErrors {
  const seen = new Set<string>();
  const mappings = d.mappings.map((m) => {
    const from = m.from.trim().toLowerCase();
    if (!from) return "填写原 sender。";
    if (seen.has(from)) return "原 sender 重复（不区分大小写）。";
    seen.add(from);
    return senderTargetError(m.to.trim());
  });
  const patterns = d.patterns.map((p) => (!p.pattern ? "填写正则。" : p.to.includes("$") ? undefined : senderTargetError(p.to.trim())));
  return { mappings, patterns };
}

/** 校验并换算：条件为空表示不限，至少要有映射、正则或默认替换中的一种。 */
export function senderPolicyInput(d: SenderPolicyDraft): { body?: SenderPolicyInput; errors: Errors<SenderPolicyDraft> } {
  const errors: Errors<SenderPolicyDraft> = {};
  const priority = Number(d.priority);
  if (!d.name.trim()) errors.name = "填写名称。";
  if (!/^\d+$/.test(d.priority) || priority > MAX_PRIORITY) errors.priority = `0 到 ${MAX_PRIORITY} 的整数，越小越优先。`;
  if (d.mcc && !validMcc(d.mcc)) errors.mcc = "三位数字，留空表示不限。";
  if (d.mnc && !validMnc(d.mnc)) errors.mnc = "两到三位数字，留空表示不限。";
  else if (d.mnc && !d.mcc) errors.mnc = "填了 MNC 就要填 MCC。";
  if (!d.mappings.length && !d.patterns.length && !d.default_enabled) errors.default_enabled = "映射、正则与默认替换至少要有一种。";
  if (d.default_enabled) {
    const e = senderTargetError(d.default_target.trim());
    if (e) errors.default_target = e;
  }
  const rows = senderPolicyRowErrors(d);
  if (rows.mappings.some(Boolean)) errors.mappings = "映射有错误，见对应行。";
  if (rows.patterns.some(Boolean)) errors.patterns = "正则有错误，见对应行。";
  if (Object.keys(errors).length) return { errors };
  return {
    errors,
    body: {
      name: d.name.trim(), priority, account_ids: d.account_ids.map(Number), channel_ids: d.channel_ids.map(Number), mcc: d.mcc, mnc: d.mnc,
      mappings: d.mappings.map((m) => ({ from: m.from.trim(), to: m.to.trim() })), patterns: d.patterns.map((p) => ({ pattern: p.pattern, to: p.to.trim() })), // 正则原样提交：首尾空白在正则里有含义
      default_enabled: d.default_enabled, default_target: d.default_enabled ? d.default_target.trim() : "", status: d.status, note: d.note.trim() || null,
    },
  };
}

/** 列表里的规则摘要：几条映射、几条正则、默认替换为什么。 */
export function senderRulesSummary(p: Pick<SenderPolicy, "mappings" | "patterns" | "default_enabled" | "default_target">): string {
  const parts: string[] = [];
  if (p.mappings.length) parts.push(`映射 ${p.mappings.length} 条`);
  if (p.patterns.length) parts.push(`正则 ${p.patterns.length} 条`);
  if (p.default_enabled) parts.push(`默认 → ${senderLabel(p.default_target)}`);
  return parts.join("，");
}
